import { Stack } from "@mui/material";
import type { FC } from "react";
import { usePropertyDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePropertyDocumentsQuery";
import {
	ClientPropertyCategoryEnum,
	type ClientPropertyLandDetailResponseDTO,
	ClientPropertyOwnershipEnum,
	ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PropertyDataCard } from "web_myplann/Portfolio/Components/PortfolioTools/PropertyDataCard";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { DataCardPreset } from "web_myplann/Shared/Components/DataCard";
import { DataNote } from "web_myplann/Shared/Components/DataNote";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import {
	DataRowCard,
	type DataRowCardItem,
} from "web_myplann/Shared/Components/DataRowCard";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { unitFormatter } from "web_myplann/Shared/Utils/numberFormatters";
import { UNITS } from "web_myplann/Shared/Utils/units";

const PageResources = Resources.PortfolioDetail;

type Props = {
	data: ClientPropertyLandDetailResponseDTO;
	currencySign: string;
	propertyId?: number;
};

export const Land: FC<Props> = ({ data, currencySign, propertyId }) => {
	const { t } = useResource();

	const { data: documents } = usePropertyDocumentsQuery(propertyId);

	const ownership =
		data?.ownership && data.ownership !== ClientPropertyOwnershipEnum.None
			? t(PageResources.Property.OwnershipValue[data.ownership])
			: "";

	const landType =
		data.propertyCategory &&
		data.propertyCategory !== ClientPropertyCategoryEnum.None
			? t(PageResources.Property.LandTypeEnum[data.propertyCategory])
			: "";

	const basicInfoItems: DataRowCardItem[] = [
		{
			label: t(PageResources.Property.LandType),
			value: landType,
		},
		{
			label: t(PageResources.Property.StreetAndNumber),
			value: data.location,
		},
		{
			label: t(PageResources.Property.Municipality),
			value: data.locationMunicipality,
		},
		{
			label: t(PageResources.Property.ZipCode),
			value: data.locationZipCode,
		},
	];

	const propertyParametersItems: DataRowCardItem[] = [
		{
			label: t(PageResources.Property.LandArea),
			value: unitFormatter(data.landSize, UNITS.squareMeter),
		},
		{
			label: t(PageResources.Property.Ownership),
			value: ownership,
		},
	];

	const bottomComponent =
		data.purchaseDate || data.description ? (
			<Stack>
				<DataRow
					label={t(PageResources.Property.PurchaseDate)}
					value={dateFormatter(data.purchaseDate)}
				/>
				<DataNote
					label={t(PageResources.Property.AdditionalDescription)}
					text={data.description ?? ""}
				/>
			</Stack>
		) : null;

	return (
		<Stack>
			<DataRowCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={bottomComponent}
				items={basicInfoItems}
			/>

			<PropertyDataCard
				reproductionValue={null}
				interiorPrice={null}
				marketValue={data.landValue}
				priceHistory={data.estimatedPriceHistoryList}
				currencySign={currencySign}
				type={ClientPropertyTypeEnum.Land}
			/>

			<DataRowCard
				preset={DataCardPreset.PropertyParameters}
				items={propertyParametersItems}
			/>

			<RelatedSection
				documents={documents?.data}
				related={data.related}
				portfolioId={data.id}
			/>
		</Stack>
	);
};
